import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ShoppingCart, Package, Box, Star, Filter, Plus, Check, Loader2, Shield } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../config";

const StorePage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGamma, setSelectedGamma] = useState("all");
  const [addingId, setAddingId] = useState(null);
  const { addToCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch {
        toast.error("Error al cargar productos");
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filtered = selectedGamma === "all" ? products : products.filter(p => p.gamma === selectedGamma);

  const gammaGroups = {
    alta: { name: "Serie Mil®", color: "#2E7D32", badge: "PREMIUM", tagline: "La más fuerte y especializada del mercado" },
    media: { name: "Reforzado", color: "#F57C00", badge: "POPULAR", tagline: "La mejor relación resistencia/precio" },
    baja: { name: "Karto-Archivo®", color: "#5D4037", badge: "ECONÓMICO", tagline: "Para que archivar ya no te cueste más" },
  };

  const handleAdd = async (productId, packType = "individual") => {
    if (!user) {
      toast.error("Inicia sesión para agregar productos al carrito");
      navigate("/login");
      return;
    }
    setAddingId(`${productId}-${packType}`);
    try {
      await addToCart(productId, 1, packType);
      toast.success("Producto agregado al carrito");
    } catch {
      toast.error("Error al agregar al carrito");
    } finally {
      setTimeout(() => setAddingId(null), 600);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="store-page">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">TIENDA OFICIAL</span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#5D4037] mt-3">
            Cajas Archivo Muerto<sup>®</sup>
          </h1>
          <p className="text-stone-600 mt-4 max-w-2xl mx-auto">
            Cajas de alta especialización para archivo de concentración. Diseño patentado desde 1967. 100% reciclable.
          </p>
        </div>

        {/* Eco Badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-3 bg-[#2E7D32]/10 px-5 py-2.5 rounded-full">
            <Shield size={18} className="text-[#2E7D32]" />
            <span className="text-xs font-semibold text-[#2E7D32]">100%R® — Reciclable · Reciclado · Biodegradable</span>
          </div>
        </div>

        {/* Gamma Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {[
            { value: "all", label: "Todos", color: "#5D4037" },
            { value: "alta", label: "Serie Mil® (Premium)", color: "#2E7D32" },
            { value: "media", label: "Reforzado (Popular)", color: "#F57C00" },
            { value: "baja", label: "Karto-Archivo® (Económico)", color: "#5D4037" },
          ].map((f) => (
            <button key={f.value} onClick={() => setSelectedGamma(f.value)} data-testid={`filter-${f.value}`}
              className={`px-4 py-2 rounded-full font-medium transition-all text-sm ${
                selectedGamma === f.value
                  ? "text-white shadow-lg scale-105"
                  : "bg-white text-stone-600 hover:bg-stone-100 border border-stone-200"
              }`}
              style={selectedGamma === f.value ? { backgroundColor: f.color } : {}}>
              {f.label}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 size={32} className="animate-spin text-stone-400" />
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {filtered.map((product, idx) => {
              const gamma = gammaGroups[product.gamma] || gammaGroups.media;
              return (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all group"
                  data-testid={`product-card-${product.id}`}
                >
                  {/* Image */}
                  <div className="relative aspect-square bg-stone-100 overflow-hidden">
                    {product.image ? (
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={48} className="text-stone-300" />
                      </div>
                    )}
                    {/* Gamma Badge */}
                    <span className="absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full text-white shadow-sm"
                      style={{ backgroundColor: gamma.color }}>
                      {gamma.badge}
                    </span>
                    {/* Model Number */}
                    <span className="absolute top-3 right-3 font-mono text-[10px] font-bold bg-white/90 backdrop-blur-sm text-stone-600 px-2 py-1 rounded-full">
                      {product.id}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="p-4">
                    <h3 className="font-bold text-[#5D4037] text-sm leading-tight mb-1">{product.name}</h3>
                    <p className="text-xs text-stone-400 mb-3">{product.format} · {product.sizes?.[0]}</p>

                    {/* Specs */}
                    <div className="flex gap-2 mb-3">
                      <span className="text-[10px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">ECT {product.ect}</span>
                      <span className="text-[10px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full">Estiba: {product.estiba}</span>
                    </div>

                    {/* Prices */}
                    <div className="space-y-2">
                      {/* Individual */}
                      <div className="flex items-center justify-between">
                        <div>
                          <span className="text-lg font-black text-[#F57C00]">${product.price?.toFixed(2)}</span>
                          <span className="text-xs text-stone-400 ml-1">/ pieza</span>
                        </div>
                        <button
                          onClick={() => handleAdd(product.id, "individual")}
                          disabled={addingId === `${product.id}-individual`}
                          className="flex items-center gap-1 bg-[#F57C00] hover:bg-[#E65100] text-white text-xs font-semibold px-3 py-2 rounded-full transition-all disabled:opacity-50"
                          data-testid={`add-to-cart-${product.id}`}
                        >
                          {addingId === `${product.id}-individual` ? <Check size={14} /> : <Plus size={14} />}
                          {addingId === `${product.id}-individual` ? "Listo" : "Agregar"}
                        </button>
                      </div>

                      {/* Pack */}
                      {product.price_pack && (
                        <div className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2">
                          <div>
                            <span className="text-sm font-bold text-[#5D4037]">${product.price_pack?.toFixed(2)}</span>
                            <span className="text-[10px] text-stone-400 ml-1">/ Econo-Pak ({product.pack_qty} pzas)</span>
                          </div>
                          <button
                            onClick={() => handleAdd(product.id, "pack")}
                            disabled={addingId === `${product.id}-pack`}
                            className="flex items-center gap-1 bg-[#5D4037] hover:bg-[#3E2723] text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all disabled:opacity-50"
                            data-testid={`add-pack-${product.id}`}
                          >
                            {addingId === `${product.id}-pack` ? <Check size={12} /> : <ShoppingCart size={12} />}
                            Pack
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Comparison Table */}
        <div className="mt-16 bg-white rounded-2xl border border-stone-200 p-6 md:p-8 overflow-x-auto">
          <h3 className="text-xl font-bold text-[#5D4037] mb-6">Comparativa de Gamas</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200">
                <th className="text-left py-3 pr-4 text-stone-500 font-semibold">Característica</th>
                <th className="text-center py-3 px-4"><span className="font-bold text-[#5D4037]">Karto-Archivo®</span></th>
                <th className="text-center py-3 px-4"><span className="font-bold text-[#F57C00]">Reforzado</span></th>
                <th className="text-center py-3 px-4"><span className="font-bold text-[#2E7D32]">Serie Mil®</span></th>
              </tr>
            </thead>
            <tbody className="text-stone-700">
              {[
                { label: "ECT Rating", baja: "20-23 lb/pulg", media: "29 lb/pulg", alta: "40 lb/pulg" },
                { label: "Estiba garantizada", baja: "2 cajas", media: "4 cajas", alta: "8 cajas" },
                { label: "A.I.R. TC® Reforzado", baja: "—", media: "✓", alta: "✓" },
                { label: "Super-Estructura H®", baja: "—", media: "—", alta: "✓" },
                { label: "Precio desde", baja: "$32", media: "$42", alta: "$89" },
              ].map((row, i) => (
                <tr key={i} className="border-b border-stone-100">
                  <td className="py-3 pr-4 font-medium">{row.label}</td>
                  <td className="py-3 px-4 text-center">{row.baja}</td>
                  <td className="py-3 px-4 text-center">{row.media}</td>
                  <td className="py-3 px-4 text-center font-semibold">{row.alta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StorePage;
